import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  Chip,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

const DUPLICATE_TEMPLATE_MUTATION = gql`
  mutation DuplicateEmailTemplate($templateId: ID!, $targetHosts: [String!]!) {
    duplicate_email_template(templateId: $templateId, targetHosts: $targetHosts) {
      results {
        host
        success
        recordId
        error
      }
      errors
    }
  }
`;

const DUPLICATE_SEQUENCE_MUTATION = gql`
  mutation DuplicateEmailSequence($sequenceId: ID!, $targetHosts: [String!]!) {
    duplicate_email_sequence(sequenceId: $sequenceId, targetHosts: $targetHosts) {
      results {
        host
        success
        recordId
        error
      }
      errors
    }
  }
`;

const ResultsView = ({ results }) => (
  <Box>
    <Typography variant="subtitle1" gutterBottom>
      Duplication results:
    </Typography>
    {results.map((r) => (
      <Box key={r.host} display="flex" alignItems="center" mb={1}>
        {r.success ? (
          <CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} />
        ) : (
          <ErrorIcon style={{ color: '#f44336', marginRight: 8 }} />
        )}
        <Typography variant="body2" style={{ flex: 1 }}>
          <strong>{r.host}</strong>
          {r.success ? ` — copied (ID: ${r.recordId})` : ` — ${r.error}`}
        </Typography>
        <Chip
          label={r.success ? 'Success' : 'Failed'}
          size="small"
          style={{
            backgroundColor: r.success ? '#4caf50' : '#f44336',
            color: 'white',
            marginLeft: 8,
          }}
        />
      </Box>
    ))}
  </Box>
);

const TenantPickerModal = ({
  open,
  onClose,
  tenants,
  recordId,
  recordType,
  currentHost,
}) => {
  const [selectedHosts, setSelectedHosts] = useState([]);
  const [results, setResults] = useState(null);

  const mutation = recordType === 'template' ? DUPLICATE_TEMPLATE_MUTATION : DUPLICATE_SEQUENCE_MUTATION;
  const availableTenants = tenants.filter((t) => t.host !== currentHost);

  const toggleHost = (host) => {
    setSelectedHosts((prev) =>
      prev.includes(host) ? prev.filter((h) => h !== host) : [...prev, host]
    );
  };

  const handleClose = () => {
    setSelectedHosts([]);
    setResults(null);
    onClose();
  };

  const entityLabel = recordType === 'template' ? 'Template' : 'Sequence';

  return (
    <Mutation
      mutation={mutation}
      onCompleted={(data) => {
        const payload =
          recordType === 'template'
            ? data?.duplicate_email_template
            : data?.duplicate_email_sequence;
        setResults(payload?.results || []);
      }}
      onError={(err) => {
        setResults([{ host: 'Error', success: false, record_id: null, error: err.message }]);
      }}
    >
      {(duplicate, { loading }) => {
        const handleConfirm = () => {
          if (selectedHosts.length === 0) return;
          const variables =
            recordType === 'template'
              ? { templateId: recordId, targetHosts: selectedHosts }
              : { sequenceId: recordId, targetHosts: selectedHosts };
          duplicate({ variables });
        };

        return (
          <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Duplicate {entityLabel} to Tenant</DialogTitle>

            <DialogContent>
              {results ? (
                <ResultsView results={results} />
              ) : (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Select one or more tenants to copy this {entityLabel.toLowerCase()} to. A new
                    copy will be created in each selected tenant.
                  </Typography>
                  {availableTenants.length === 0 ? (
                    <Typography variant="body2">No other tenants available.</Typography>
                  ) : (
                    <List dense>
                      {availableTenants.map((tenant) => (
                        <ListItem
                          key={tenant.host}
                          button
                          onClick={() => toggleHost(tenant.host)}
                        >
                          <ListItemText primary={tenant.name} secondary={tenant.host} />
                          <ListItemSecondaryAction>
                            <Checkbox
                              edge="end"
                              checked={selectedHosts.includes(tenant.host)}
                              onChange={() => toggleHost(tenant.host)}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose} color="default">
                {results ? 'Close' : 'Cancel'}
              </Button>
              {!results && (
                <Button
                  onClick={handleConfirm}
                  color="primary"
                  variant="contained"
                  disabled={selectedHosts.length === 0 || loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  {loading
                    ? 'Duplicating…'
                    : `Duplicate to ${selectedHosts.length || ''} tenant${selectedHosts.length === 1 ? '' : 's'}`}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        );
      }}
    </Mutation>
  );
};

TenantPickerModal.propTypes = {
  open:        PropTypes.bool.isRequired,
  onClose:     PropTypes.func.isRequired,
  tenants:     PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    host: PropTypes.string.isRequired,
  })).isRequired,
  recordId:    PropTypes.string.isRequired,
  recordType:  PropTypes.oneOf(['template', 'sequence']).isRequired,
  currentHost: PropTypes.string,
};

TenantPickerModal.defaultProps = {
  currentHost: '',
};

export default TenantPickerModal;
